import { Platform } from '@angular/cdk/platform';
import { isPlatformBrowser, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControlDirective, FormGroupDirective } from '@angular/forms';
import { Subscription } from 'rxjs';

import { DropdownBase } from '../../dropdown/dropdown.base';
import { AppIcon } from '../../icons/icon.type';
import { IconsModule } from '../../icons/icons.module';
import { AppIconsLibrary } from '../../icons/icons-library.service';
import { InputComponent } from '../input.component';
import { fromWindowEvent } from 'core/utils/rxjs/window-event';

export type ExtrasType = 'search' | 'clear' | 'valid' | 'dropdown' | 'password' | 'search-hotkey' | 'datepicker';

type ExtrasButtonData = { action: ExtrasType; icon: AppIcon | undefined; hidden: WritableSignal<boolean> };

@Component({
  selector: 'app-input-extras',
  template: `
    @for (b of buttons; track $index) {
      <app-icon
        class="extras"
        [ngClass]="b.hidden() ? 'hidden' : ''"
        [icon]="b.icon"
        tabindex="-1"
        (mousedown)="handleExtrasClick(b)"
        (click)="$event.stopPropagation()"></app-icon>
    }
  `,
  standalone: true,
  imports: [IconsModule, NgClass],
  styleUrls: ['input-extras.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputExtrasComponent implements OnChanges, OnInit, OnDestroy {
  @Input()
  extras?: ExtrasType[];

  buttons: ExtrasButtonData[] = [];

  private _destroyRef = inject(DestroyRef);
  private _searchShortcut = 'Control+f';
  private _searchHotkeySub = new Subscription();
  private _IS_MACOS = false;


  constructor(
    private input: InputComponent,
    private platform: Platform,
    private icons: AppIconsLibrary,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() private dropdown?: DropdownBase,
    @Optional() private formGroup?: FormGroupDirective,
    @Optional() private formControl?: FormControlDirective,
  ) {
    this._searchShortcut = this.getSearchShortcut();
    if(isPlatformBrowser(platformId)) {
      this._IS_MACOS = /Macintosh/i.test(window.navigator.userAgent);
    }
  }

  ngOnDestroy(): void {
    this._searchHotkeySub.unsubscribe();
  }

  ngOnInit(): void {
    this.input.changed$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.updateVisibility();
    });
    this.updateVisibility();
    this.formControl?.valueChanges?.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.updateVisibilityByControl();
    });
    this.updateVisibilityByControl();
  }

  ngOnChanges(): void {
    this.buttons = [];
    this._searchHotkeySub.unsubscribe();
    this.extras?.forEach(e => {
      switch (e) {
        case 'clear': {
          this.buttons.push({ action: e, icon: this.icons.getIcon('close'), hidden: signal(false) });
          break;
        }
         case 'valid': {
          this.buttons.push({ action: e, icon: this.icons.getIcon('valid'), hidden: signal(false) });
          break;
        }
        case 'search': {
          this.buttons.push({ action: e, icon: this.icons.getIcon('search'), hidden: signal(false) });
          break;
        }
        case 'datepicker': {
          this.buttons.push({ action: e, icon: this.icons.getIcon('calendar'), hidden: signal(false) });
          break;
        }
        case 'dropdown': {
          this.buttons.push({ action: e, icon: this.icons.getIcon('angle-down'), hidden: signal(false) });
          break;
        }
        case 'password': {
          this.buttons.push({ action: e, icon: this.icons.getIcon('eye'), hidden: signal(false) });
          break;
        }
        case 'search-hotkey': {
          if (this.platform.isBrowser && this._IS_MACOS) {
            this.buttons.push({ action: e, icon: this.icons.getIcon('search-hotkey'), hidden: signal(false) });
          }
          this.observeKeyboardEvent();
          break;
        }
      }
    });
  }

  private updateVisibilityByControl() {
    this.buttons.find(b => b.action === 'valid')?.hidden.set(!this.formControl?.valid);
  }

  private getSearchShortcut(): string {
    if (isPlatformBrowser(this.platformId)) {
      if (this._IS_MACOS) {
        return 'Meta+f';
      } else {
        return 'Control+f';
      }
    }
    return 'Control+f';
  }

  observeKeyboardEvent() {
    this._searchHotkeySub = fromWindowEvent('keydown')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(event => {
        if (this.isShortcutPressed(event, this._searchShortcut)) {
          event.preventDefault();
          this.input.focus();
        }
      });
  }

  private isShortcutPressed(event: KeyboardEvent, shortcut: string): boolean {
    const [key, ...modifiers] = shortcut.split('+').reverse();
    return event.key === key && modifiers.every(modifier => event.getModifierState(modifier));
  }

  private updateVisibility() {
    this.buttons.forEach(btn => {
      if (btn.action === 'clear') {
        btn.hidden?.set(!this.input.inputRef.nativeElement.value.length);
      } else if (btn.action === 'search-hotkey') {
        btn.hidden?.set(!!this.input.inputRef.nativeElement.value.length);
      }
    });
  }

  handleExtrasClick(btn: ExtrasButtonData): void {
    if (btn.action === 'datepicker' && !this.dropdown?.anchorRef?.anchorEnabled) {
      if (!this.dropdown?.active) {
        // iOS fix
        this.dropdown?.stopClose();
      }
      this.dropdown?.toggle();
    } else if (btn.action === 'search' && this.formGroup) {
      this.formGroup.onSubmit(this.formGroup.value);
    } else if (btn.action === 'clear') {
      this.input.clear();
    } else if (btn.action === 'password') {
      if (this.input.type === 'password') {
        this.input.type = 'text';
        btn.icon = this.icons.getIcon('eye-slash');
      } else {
        this.input.type = 'password';
        btn.icon = this.icons.getIcon('eye');
      }
    }
    // do refocus if input was focused before click on extras
    if (this.input.focused) {
      requestAnimationFrame(() => {
        this.input.focus();
      });
    }
  }
}
